import React, { useState } from "react";
import {
  act,
  fireEvent,
  render,
  screen,
  waitFor,
  within,
} from "@testing-library/react";
import "@testing-library/jest-dom";
import { FilterUI, FilterInterface } from "./SearchFilters";
import {
  buildFilterDoc,
  filterValuesFromDoc,
  type FilterValues,
} from "./filterState";

// Name lookups ask the server; this stands in for it.
const mockNameOptionsQuery = jest.fn();
const mockNameLookup = jest.fn();
jest.mock("../hooks/searchQueries", () => ({
  useNameOptionsQuery: (...args: unknown[]) => mockNameOptionsQuery(...args),
  useNameLookup: () => mockNameLookup,
}));
jest.mock("react-router-dom", () => ({
  ...jest.requireActual("react-router-dom"),
  useParams: () => ({ workspace_id: "ws-1" }),
}));

const ENUM_PATH = "value.group_a.colour.value";
const OTHER_PATH = "value.group_b.shape.value";

const schema: FilterInterface = {
  path: "value",
  children: [
    {
      path: "value.group_a",
      children: [
        {
          path: ENUM_PATH,
          type: "string",
          allowed_values: ["Red", "Green", "Blue"],
        },
      ],
    },
    {
      path: "value.group_b",
      children: [
        {
          path: OTHER_PATH,
          type: "string",
          allowed_values: ["Round", "Square"],
        },
      ],
    },
  ],
};

/** The doc FilterUI produced for "colour = Green", as a ?f= link carries it. */
const greenDoc = {
  path: "value",
  children: [
    {
      path: "value.group_a",
      children: [
        { path: ENUM_PATH, type: "string", allowed_values: ["Green"] },
      ],
    },
  ],
};

/**
 * Mounted filter inputs, INCLUDING those inside collapsed groups.
 *
 * `hidden: true` is the whole point. Joy mounts collapsed content and hides it,
 * so a default (visible-only) role query would count zero either way and the
 * lazy-mount test would pass without proving anything. Short lists render one
 * checkbox per option, so a leaf can count more than once.
 */
const mountedInputs = () =>
  ["combobox", "checkbox", "radio", "spinbutton"].flatMap((role) =>
    screen.queryAllByRole(role, { hidden: true }),
  );

/** Holds filter state the way the search views do, and reports every change. */
const Harness = ({
  filterData = schema,
  initial = {},
  onValues = () => {},
  primaryFacets,
  search,
}: {
  filterData?: FilterInterface;
  initial?: FilterValues;
  onValues?: (values: FilterValues) => void;
  primaryFacets?: readonly string[];
  search?: string;
}) => {
  const [values, setValues] = useState<FilterValues>(initial);
  return (
    <FilterUI
      filterData={filterData}
      value={values}
      onChange={(next) => {
        setValues(next);
        onValues(next);
      }}
      primaryFacets={primaryFacets}
      search={search}
    />
  );
};

/** A schema holding just the given leaves, at the top level. */
const leaves = (...children: FilterInterface[]): FilterInterface => ({
  path: "value",
  children,
});

describe("FilterUI lazy accordion contents", () => {
  it("mounts no filter inputs while every group is collapsed", () => {
    render(<Harness />);
    expect(mountedInputs()).toHaveLength(0);
  });

  it("shows the top-level groups directly, not inside a root group", () => {
    render(<Harness />);

    expect(screen.getByRole("button", { name: "Group a" })).toBeInTheDocument();
    expect(
      screen.queryByRole("button", { name: /^Filters/ }),
    ).not.toBeInTheDocument();
  });

  it("mounts a group's inputs when it is first opened", () => {
    render(<Harness />);

    fireEvent.click(screen.getByRole("button", { name: "Group a" }));

    // group_a's one leaf is now live, as three checkboxes; group_b is still unopened.
    expect(mountedInputs()).toHaveLength(3);
  });

  it("opens the groups that carry filters from a shared link and leaves the rest shut", () => {
    render(<Harness initial={filterValuesFromDoc(greenDoc)} />);

    expect(screen.getByRole("button", { name: /^Group a/ })).toHaveAttribute(
      "aria-expanded",
      "true",
    );
    expect(screen.getByRole("button", { name: "Group b" })).toHaveAttribute(
      "aria-expanded",
      "false",
    );
    expect(mountedInputs()).toHaveLength(3);
  });

  it("shows how many active filters each group holds", () => {
    render(<Harness initial={filterValuesFromDoc(greenDoc)} />);

    expect(
      screen.getByRole("button", { name: /^Group a.*1 active/ }),
    ).toBeInTheDocument();
    expect(screen.getByRole("button", { name: "Group b" })).toBeInTheDocument();
  });

  it("keeps a selection when its group is collapsed and reopened", () => {
    const onValues = jest.fn();
    render(
      <Harness initial={filterValuesFromDoc(greenDoc)} onValues={onValues} />,
    );

    const groupA = screen.getByRole("button", { name: /^Group a/ });
    fireEvent.click(groupA);
    expect(groupA).toHaveAttribute("aria-expanded", "false");
    fireEvent.click(groupA);

    expect(screen.getByRole("checkbox", { name: "Green" })).toBeChecked();
    expect(onValues).not.toHaveBeenCalled();
  });

  it("reports a picked value as the node it adds to the filter doc", () => {
    const onValues = jest.fn();
    render(<Harness onValues={onValues} />);

    fireEvent.click(screen.getByRole("button", { name: "Group a" }));
    fireEvent.click(screen.getByRole("checkbox", { name: "Green" }));

    expect(onValues).toHaveBeenLastCalledWith({
      [ENUM_PATH]: {
        path: ENUM_PATH,
        type: "string",
        allowed_values: ["Green"],
      },
    });
  });
});

describe("FilterUI with primary facets", () => {
  const PRIMARY = [OTHER_PATH];
  const round: FilterValues = {
    [OTHER_PATH]: {
      path: OTHER_PATH,
      type: "string",
      allowed_values: ["Round"],
    },
  };

  it("shows a primary facet's input straight away and the rest behind More filters", () => {
    render(<Harness primaryFacets={PRIMARY} />);

    // The primary facet is live, and nothing else is mounted.
    expect(screen.getAllByRole("checkbox")).toHaveLength(2);
    expect(mountedInputs()).toHaveLength(2);
    expect(
      screen.getByRole("button", { name: "More filters" }),
    ).toHaveAttribute("aria-expanded", "false");
    // Its group had nothing else in it, so it is gone.
    expect(
      screen.queryByRole("button", { name: /^Group b/ }),
    ).not.toBeInTheDocument();
  });

  it("keeps the remaining groups inside More filters", () => {
    render(<Harness primaryFacets={PRIMARY} />);

    fireEvent.click(screen.getByRole("button", { name: "More filters" }));

    expect(screen.getByRole("button", { name: "Group a" })).toBeInTheDocument();
    expect(mountedInputs()).toHaveLength(2);
  });

  it("opens More filters for a shared link that sets a filter inside it", () => {
    render(
      <Harness
        primaryFacets={PRIMARY}
        initial={filterValuesFromDoc(greenDoc)}
      />,
    );

    expect(
      screen.getByRole("button", { name: /^More filters.*1 active/ }),
    ).toHaveAttribute("aria-expanded", "true");
  });

  it("leaves a primary facet's filter out of the More filters count", () => {
    render(<Harness primaryFacets={PRIMARY} initial={round} />);

    expect(
      screen.getByRole("button", { name: "More filters" }),
    ).toHaveAttribute("aria-expanded", "false");
    expect(screen.getByRole("checkbox", { name: "Round" })).toBeChecked();
  });

  it("reports a primary pick as the same node the grouped input sends", () => {
    const onValues = jest.fn();
    render(<Harness primaryFacets={PRIMARY} onValues={onValues} />);

    fireEvent.click(screen.getByRole("checkbox", { name: "Square" }));

    expect(onValues).toHaveBeenLastCalledWith({
      [OTHER_PATH]: {
        path: OTHER_PATH,
        type: "string",
        allowed_values: ["Square"],
      },
    });
  });
});

describe("FilterUI while searching", () => {
  it("shows the matching filters as live inputs, under the group they sit in", () => {
    render(<Harness search="shape" />);

    expect(screen.getByRole("status")).toHaveTextContent("1 matching filter");
    const list = screen.getByRole("list", { name: "Matching filters" });
    expect(within(list).getByText("Group b")).toBeVisible();
    expect(within(list).getByText("Shape")).toBeVisible();
    expect(screen.getAllByRole("checkbox")).toHaveLength(2);
    // The tree is out of sight while the search is on.
    expect(
      screen.queryByRole("button", { name: "Group a" }),
    ).not.toBeInTheDocument();
  });

  it("finds filters that sit behind More filters", () => {
    render(<Harness primaryFacets={[OTHER_PATH]} search="colour" />);

    expect(screen.getByText("Colour")).toBeVisible();
    expect(screen.getAllByRole("checkbox")).toHaveLength(3);
  });

  it("says which option matched when the name didn't", () => {
    render(<Harness search="green" />);

    expect(screen.getByText("Group a · Has “Green”")).toBeVisible();
  });

  it("reports a pick from a match as the node the tree would send", () => {
    const onValues = jest.fn();
    render(<Harness search="colour" onValues={onValues} />);

    fireEvent.click(screen.getByRole("checkbox", { name: "Green" }));

    expect(onValues).toHaveBeenLastCalledWith({
      [ENUM_PATH]: {
        path: ENUM_PATH,
        type: "string",
        allowed_values: ["Green"],
      },
    });
  });

  it("says so when nothing matches", () => {
    render(<Harness search="  zebra " />);

    expect(screen.getByRole("status")).toHaveTextContent(
      "No filters match “zebra”.",
    );
    expect(screen.queryAllByRole("checkbox")).toHaveLength(0);
  });

  it("brings the tree back as it was once the search is cleared", () => {
    const { rerender } = render(<Harness />);
    fireEvent.click(screen.getByRole("button", { name: "Group a" }));

    rerender(<Harness search="shape" />);
    rerender(<Harness search="" />);

    expect(screen.getByRole("button", { name: "Group a" })).toHaveAttribute(
      "aria-expanded",
      "true",
    );
    expect(screen.queryByRole("status")).not.toBeInTheDocument();
  });
});

describe("the control each filter type gets", () => {
  afterEach(() => {
    jest.useRealTimers();
  });

  describe("yes/no filters", () => {
    const BOOL = "value.is_listed.value";
    const boolSchema = leaves({
      path: BOOL,
      type: "boolean",
      allowed_values: [true, false],
    });

    it("offers Any, Yes and No, with Any picked while unset", () => {
      render(<Harness filterData={boolSchema} />);

      const group = screen.getByRole("radiogroup", { name: "Is listed" });
      expect(
        within(group)
          .getAllByRole("radio")
          .map((radio) => radio.getAttribute("value")),
      ).toEqual(["Any", "Yes", "No"]);
      expect(within(group).getByRole("radio", { name: "Any" })).toBeChecked();
    });

    it("sends the same node the old dropdown did, and Any clears it", () => {
      const onValues = jest.fn();
      render(<Harness filterData={boolSchema} onValues={onValues} />);

      fireEvent.click(screen.getByRole("radio", { name: "No" }));
      expect(onValues).toHaveBeenLastCalledWith({
        [BOOL]: { path: BOOL, type: "boolean", allowed_values: [false] },
      });

      fireEvent.click(screen.getByRole("radio", { name: "Any" }));
      expect(onValues).toHaveBeenLastCalledWith({});
    });

    it("shows a shared link's value", () => {
      render(
        <Harness
          filterData={boolSchema}
          initial={{
            [BOOL]: { path: BOOL, type: "boolean", allowed_values: [true] },
          }}
        />,
      );

      expect(screen.getByRole("radio", { name: "Yes" })).toBeChecked();
    });
  });

  describe("lists of up to ten options", () => {
    const STAGE = "value.stage_focus.value";
    const stages = Array.from({ length: 10 }, (_, i) => `Stage ${i + 1}`);
    const stageSchema = leaves({
      path: STAGE,
      type: "keyword",
      allowed_values: stages,
    });

    it("shows the first five and folds the rest behind Show more", () => {
      render(<Harness filterData={stageSchema} />);

      expect(screen.getAllByRole("checkbox")).toHaveLength(5);
      fireEvent.click(screen.getByRole("button", { name: "Show 5 more" }));
      expect(screen.getAllByRole("checkbox")).toHaveLength(10);
      expect(
        screen.getByRole("button", { name: "Show fewer" }),
      ).toBeInTheDocument();
    });

    it("never folds away a picked option", () => {
      render(
        <Harness
          filterData={stageSchema}
          initial={{
            [STAGE]: {
              path: STAGE,
              type: "keyword",
              allowed_values: ["Stage 9"],
            },
          }}
        />,
      );

      expect(screen.getAllByRole("checkbox")).toHaveLength(6);
      expect(screen.getByRole("checkbox", { name: "Stage 9" })).toBeChecked();
      expect(
        screen.getByRole("button", { name: "Show 4 more" }),
      ).toBeInTheDocument();
    });

    it("unticking the last pick clears the filter", () => {
      const onValues = jest.fn();
      render(<Harness filterData={stageSchema} onValues={onValues} />);

      const first = screen.getByRole("checkbox", { name: "Stage 1" });
      fireEvent.click(first);
      fireEvent.click(screen.getByRole("checkbox", { name: "Stage 2" }));
      expect(onValues).toHaveBeenLastCalledWith({
        [STAGE]: {
          path: STAGE,
          type: "keyword",
          allowed_values: ["Stage 1", "Stage 2"],
        },
      });

      fireEvent.click(first);
      fireEvent.click(screen.getByRole("checkbox", { name: "Stage 2" }));
      expect(onValues).toHaveBeenLastCalledWith({});
    });

    it("keeps a numeric option's value as a number", () => {
      const YEAR = "value.founded.value";
      const onValues = jest.fn();
      render(
        <Harness
          filterData={leaves({
            path: YEAR,
            type: "integer",
            allowed_values: [2024, 2025],
          })}
          onValues={onValues}
        />,
      );

      fireEvent.click(screen.getByRole("checkbox", { name: "2025" }));

      expect(onValues).toHaveBeenLastCalledWith({
        [YEAR]: { path: YEAR, type: "integer", allowed_values: [2025] },
      });
    });
  });

  describe("longer lists", () => {
    const AREA = "value.therapeutic_area.value";
    const areas = Array.from({ length: 12 }, (_, i) => `Area ${i + 1}`);
    const areaSchema = leaves({
      path: AREA,
      type: "keyword",
      allowed_values: areas,
    });

    it("are a searchable dropdown named after the filter", () => {
      render(<Harness filterData={areaSchema} />);

      expect(
        screen.getByRole("combobox", { name: "Therapeutic area" }),
      ).toBeInTheDocument();
      expect(screen.queryByRole("checkbox")).not.toBeInTheDocument();
    });

    it("list the picked values first when opened", () => {
      render(
        <Harness
          filterData={areaSchema}
          initial={{
            [AREA]: {
              path: AREA,
              type: "keyword",
              allowed_values: ["Area 9", "Area 4"],
            },
          }}
        />,
      );

      fireEvent.mouseDown(screen.getByRole("combobox"));

      const names = screen
        .getAllByRole("option")
        .map((option) => option.textContent);
      // In schema order among themselves, then everything else.
      expect(names.slice(0, 3)).toEqual(["Area 4", "Area 9", "Area 1"]);
      expect(names).toHaveLength(12);
    });

    it("stay open for several picks in a row", () => {
      const onValues = jest.fn();
      render(<Harness filterData={areaSchema} onValues={onValues} />);

      fireEvent.mouseDown(screen.getByRole("combobox"));
      fireEvent.click(screen.getByRole("option", { name: "Area 2" }));
      fireEvent.click(screen.getByRole("option", { name: "Area 7" }));

      expect(onValues).toHaveBeenLastCalledWith({
        [AREA]: {
          path: AREA,
          type: "keyword",
          allowed_values: ["Area 2", "Area 7"],
        },
      });
    });

    it("don't reorder while the dropdown is open", () => {
      render(<Harness filterData={areaSchema} />);

      fireEvent.mouseDown(screen.getByRole("combobox"));
      fireEvent.click(screen.getByRole("option", { name: "Area 7" }));

      expect(screen.getAllByRole("option")[0]).toHaveTextContent("Area 1");
    });

    it("show each pick as a chip with its own remove button", () => {
      const onValues = jest.fn();
      render(
        <Harness
          filterData={areaSchema}
          onValues={onValues}
          initial={{
            [AREA]: {
              path: AREA,
              type: "keyword",
              allowed_values: ["Area 4", "Area 9"],
            },
          }}
        />,
      );

      // The full name stays available when a long one is truncated.
      expect(screen.getByTitle("Area 4")).toBeInTheDocument();
      fireEvent.click(screen.getByRole("button", { name: "Remove Area 4" }));

      expect(onValues).toHaveBeenLastCalledWith({
        [AREA]: {
          path: AREA,
          type: "keyword",
          allowed_values: ["Area 9"],
        },
      });
    });

    it("list at most 50 matches, so a long list stays quick", () => {
      const many = Array.from({ length: 500 }, (_, i) => `Area ${i + 1}`);
      render(
        <Harness
          filterData={leaves({
            path: AREA,
            type: "keyword",
            allowed_values: many,
          })}
        />,
      );

      const input = screen.getByRole("combobox");
      // Focused, as when typing: Autocomplete clears an unfocused input.
      act(() => input.focus());
      fireEvent.mouseDown(input);
      expect(screen.getAllByRole("option")).toHaveLength(50);

      fireEvent.change(input, {
        target: { value: "area 49" },
      });
      // "Area 49" and "Area 490" to "Area 499".
      expect(screen.getAllByRole("option")).toHaveLength(11);
    });
  });

  describe("lists looked up as you type", () => {
    const NAME = "value.name.value";
    const names = ["Acme Bio", "Acorn Therapeutics", "Zeta Pharma"];
    const nameSchema = leaves({
      path: NAME,
      type: "keyword",
      allowed_values: [],
      lookup: "company",
    });

    beforeEach(() => {
      jest.useFakeTimers();
      mockNameOptionsQuery.mockReset();
      mockNameOptionsQuery.mockImplementation(
        (_ws: string, _index: string, query: string, enabled: boolean) => ({
          data: enabled
            ? names.filter((name) =>
                name.toLowerCase().includes(query.toLowerCase()),
              )
            : undefined,
          isFetching: false,
        }),
      );
      mockNameLookup.mockReset();
      mockNameLookup.mockImplementation(async (query: string) =>
        names.filter((name) =>
          name.toLowerCase().includes(query.toLowerCase()),
        ),
      );
    });

    afterEach(() => {
      jest.useRealTimers();
    });

    it("don't ask the server until the dropdown opens", () => {
      render(<Harness filterData={nameSchema} />);

      expect(
        screen.getByRole("combobox", { name: "Name" }),
      ).toBeInTheDocument();
      expect(mockNameOptionsQuery).not.toHaveBeenCalledWith(
        expect.anything(),
        expect.anything(),
        expect.anything(),
        true,
      );
    });

    it("ask once typing pauses, and pick from what comes back", () => {
      const onValues = jest.fn();
      render(<Harness filterData={nameSchema} onValues={onValues} />);

      fireEvent.mouseDown(screen.getByRole("combobox"));
      expect(mockNameOptionsQuery).toHaveBeenLastCalledWith(
        "ws-1",
        "company",
        "",
        true,
      );

      fireEvent.change(screen.getByRole("combobox"), {
        target: { value: "ac" },
      });
      // Still the empty query: the typed one waits for the pause.
      expect(mockNameOptionsQuery).toHaveBeenLastCalledWith(
        "ws-1",
        "company",
        "",
        true,
      );

      act(() => {
        jest.advanceTimersByTime(250);
      });
      expect(mockNameOptionsQuery).toHaveBeenLastCalledWith(
        "ws-1",
        "company",
        "ac",
        true,
      );
      expect(
        screen.getAllByRole("option").map((option) => option.textContent),
      ).toEqual(["Names containing “ac”", "Acme Bio", "Acorn Therapeutics"]);

      fireEvent.click(
        screen.getByRole("option", { name: "Acorn Therapeutics" }),
      );
      expect(onValues).toHaveBeenLastCalledWith({
        [NAME]: {
          path: NAME,
          type: "keyword",
          allowed_values: ["Acorn Therapeutics"],
          lookup: "company",
        },
      });
    });

    const nameNode = (picked: string[], contains?: string[]) => ({
      [NAME]: {
        path: NAME,
        type: "keyword",
        allowed_values: picked,
        lookup: "company",
        ...(contains ? { contains } : {}),
      },
    });

    const typeName = (text: string) => {
      const input = screen.getByRole("combobox");
      act(() => input.focus());
      fireEvent.mouseDown(input);
      fireEvent.change(input, { target: { value: text } });
      return input;
    };

    it("match names containing the typed text on Enter", () => {
      const onValues = jest.fn();
      render(<Harness filterData={nameSchema} onValues={onValues} />);

      const input = typeName("acme");
      fireEvent.keyDown(input, { key: "Enter" });

      expect(onValues).toHaveBeenLastCalledWith(nameNode([], ["acme"]));
      expect(screen.getByText("contains “acme”")).toBeInTheDocument();
    });

    it("offer the typed text as the first option", () => {
      const onValues = jest.fn();
      render(
        <Harness
          filterData={nameSchema}
          onValues={onValues}
          initial={nameNode(["Zeta Pharma"])}
        />,
      );

      typeName("ac");
      fireEvent.click(
        screen.getByRole("option", { name: "Names containing “ac”" }),
      );

      expect(onValues).toHaveBeenLastCalledWith(
        nameNode(["Zeta Pharma"], ["ac"]),
      );
    });

    it("keep typed text as a match when the field loses focus", () => {
      const onValues = jest.fn();
      render(<Harness filterData={nameSchema} onValues={onValues} />);

      const input = typeName("acme");
      fireEvent.blur(input);

      expect(onValues).toHaveBeenLastCalledWith(nameNode([], ["acme"]));
      expect(input).toHaveValue("");
    });

    const pasteText = (text: string) =>
      fireEvent.paste(screen.getByRole("combobox"), {
        clipboardData: { getData: () => text },
      });

    it("add a chip per name from a pasted list: exact where the index has the name, contains otherwise", async () => {
      const onValues = jest.fn();
      render(
        <Harness
          filterData={nameSchema}
          onValues={onValues}
          initial={nameNode(["Zeta Pharma"])}
        />,
      );

      pasteText("acme bio, Acorn, Zeta Pharma");

      await waitFor(() =>
        expect(onValues).toHaveBeenLastCalledWith(
          nameNode(["Zeta Pharma", "Acme Bio"], ["Acorn"]),
        ),
      );
      expect(screen.getByText("contains “Acorn”")).toBeInTheDocument();
      expect(screen.getByRole("combobox")).toHaveValue("");
    });

    it("drop text already in the box when a list is pasted over it", async () => {
      const onValues = jest.fn();
      render(<Harness filterData={nameSchema} onValues={onValues} />);

      const input = typeName("Ac");
      pasteText("Acme Bio, Zeta Pharma");
      expect(input).toHaveValue("");

      await waitFor(() =>
        expect(onValues).toHaveBeenLastCalledWith(
          nameNode(["Acme Bio", "Zeta Pharma"]),
        ),
      );
      fireEvent.blur(input);
      expect(onValues).toHaveBeenLastCalledWith(
        nameNode(["Acme Bio", "Zeta Pharma"]),
      );
    });

    it("keep both lists when a second is pasted before the first is looked up", async () => {
      const onValues = jest.fn();
      render(<Harness filterData={nameSchema} onValues={onValues} />);

      pasteText("Acme Bio, Unknown One");
      pasteText("Zeta Pharma, Unknown Two");

      await waitFor(() =>
        expect(onValues).toHaveBeenLastCalledWith(
          nameNode(["Acme Bio", "Zeta Pharma"], ["Unknown One", "Unknown Two"]),
        ),
      );
    });

    it("read a typed list like a pasted one, on Enter", async () => {
      const onValues = jest.fn();
      render(<Harness filterData={nameSchema} onValues={onValues} />);

      const input = typeName("acme bio, Acorn");
      expect(
        screen.getByRole("option", { name: "Add 2 names" }),
      ).toBeInTheDocument();
      fireEvent.keyDown(input, { key: "Enter" });

      await waitFor(() =>
        expect(onValues).toHaveBeenLastCalledWith(
          nameNode(["Acme Bio"], ["Acorn"]),
        ),
      );
      expect(onValues).toHaveBeenCalledTimes(1);
      expect(input).toHaveValue("");
    });

    it("read a typed list when the field loses focus", async () => {
      const onValues = jest.fn();
      render(<Harness filterData={nameSchema} onValues={onValues} />);

      const input = typeName("Zeta Pharma; Acorn");
      fireEvent.blur(input);

      await waitFor(() =>
        expect(onValues).toHaveBeenLastCalledWith(
          nameNode(["Zeta Pharma"], ["Acorn"]),
        ),
      );
      expect(input).toHaveValue("");
    });

    it("leave a single pasted name to be typed text", () => {
      const onValues = jest.fn();
      render(<Harness filterData={nameSchema} onValues={onValues} />);

      const pasted = pasteText("Acme Bio");

      // Not intercepted: the browser inserts the text as usual.
      expect(pasted).toBe(true);
      expect(mockNameLookup).not.toHaveBeenCalled();
      expect(onValues).not.toHaveBeenCalled();
    });

    it("keep a name as a contains match when its lookup fails", async () => {
      mockNameLookup.mockRejectedValue(new Error("down"));
      const onValues = jest.fn();
      render(<Harness filterData={nameSchema} onValues={onValues} />);

      pasteText("Acme Bio\nZeta Pharma");

      await waitFor(() =>
        expect(onValues).toHaveBeenLastCalledWith(
          nameNode([], ["Acme Bio", "Zeta Pharma"]),
        ),
      );
    });

    it("say which names didn't fit once ten contains matches are in", async () => {
      const onValues = jest.fn();
      render(<Harness filterData={nameSchema} onValues={onValues} />);
      const unknown = Array.from({ length: 12 }, (_, i) => `Unknown ${i + 1}`);

      pasteText(["Acme Bio", ...unknown].join(", "));

      await waitFor(() =>
        expect(onValues).toHaveBeenLastCalledWith(
          nameNode(["Acme Bio"], unknown.slice(0, 10)),
        ),
      );
      expect(screen.getByRole("status")).toHaveTextContent(
        "Not added: Unknown 11, Unknown 12.",
      );
    });
  });

  describe("number ranges", () => {
    const SIZE = "value.size.value";
    const sizeSchema = leaves({ path: SIZE, type: "number", min: 0, max: 100 });

    it("take a min and a max, with the data's bounds as hints", () => {
      render(<Harness filterData={sizeSchema} />);

      expect(
        screen.getByRole("spinbutton", { name: "Size, minimum" }),
      ).toHaveAttribute("placeholder", "Min 0");
      expect(
        screen.getByRole("spinbutton", { name: "Size, maximum" }),
      ).toHaveAttribute("placeholder", "Max 100");
      expect(screen.queryByRole("slider")).not.toBeInTheDocument();
    });

    it("send a typed bound once typing stops", () => {
      jest.useFakeTimers();
      const onValues = jest.fn();
      render(<Harness filterData={sizeSchema} onValues={onValues} />);

      fireEvent.change(
        screen.getByRole("spinbutton", { name: "Size, maximum" }),
        {
          target: { value: "40" },
        },
      );
      expect(onValues).not.toHaveBeenCalled();
      act(() => {
        jest.advanceTimersByTime(300);
      });

      expect(onValues).toHaveBeenLastCalledWith({
        [SIZE]: { path: SIZE, type: "number", max: 40 },
      });
    });

    it("clear the filter when both boxes are emptied", () => {
      jest.useFakeTimers();
      const onValues = jest.fn();
      render(
        <Harness
          filterData={sizeSchema}
          initial={{ [SIZE]: { path: SIZE, type: "number", min: 10 } }}
          onValues={onValues}
        />,
      );

      const min = screen.getByRole("spinbutton", { name: "Size, minimum" });
      expect(min).toHaveValue(10);
      fireEvent.change(min, { target: { value: "" } });
      act(() => {
        jest.advanceTimersByTime(300);
      });

      expect(onValues).toHaveBeenLastCalledWith({});
    });

    it("keep a typed bound that matches the data's own, without sending it", () => {
      jest.useFakeTimers();
      const onValues = jest.fn();
      render(<Harness filterData={sizeSchema} onValues={onValues} />);

      const min = screen.getByRole("spinbutton", { name: "Size, minimum" });
      fireEvent.change(min, { target: { value: "0" } });
      act(() => {
        jest.advanceTimersByTime(300);
      });

      expect(onValues).not.toHaveBeenCalled();
      expect(min).toHaveValue(0);
    });

    it("follow a filter removed from outside", () => {
      const initial = { [SIZE]: { path: SIZE, type: "number", min: 10 } };
      const { rerender } = render(
        <FilterUI
          filterData={sizeSchema}
          value={initial}
          onChange={() => {}}
        />,
      );

      rerender(
        <FilterUI filterData={sizeSchema} value={{}} onChange={() => {}} />,
      );

      expect(
        screen.getByRole("spinbutton", { name: "Size, minimum" }),
      ).toHaveValue(null);
    });
  });

  describe("number ranges with presets", () => {
    const STAFF = "value.number_of_employees.value";
    const staffSchema = leaves({
      path: STAFF,
      type: "integer",
      min: 0,
      max: 100000,
    });

    it("fill the boxes from a preset and mark it picked", () => {
      jest.useFakeTimers();
      const onValues = jest.fn();
      render(<Harness filterData={staffSchema} onValues={onValues} />);

      const startup = screen.getByRole("button", { name: "Startup (1–50)" });
      fireEvent.click(startup);
      act(() => {
        jest.advanceTimersByTime(300);
      });

      expect(onValues).toHaveBeenLastCalledWith({
        [STAFF]: { path: STAFF, type: "integer", min: 1, max: 50 },
      });
      expect(startup).toHaveAttribute("aria-pressed", "true");
      expect(
        screen.getByRole("spinbutton", {
          name: "Number of employees, minimum",
        }),
      ).toHaveValue(1);
    });

    it("clear the filter when the picked preset is clicked again", () => {
      // It used to send a range with no bounds, which the backend ignores
      // but which left an "any" chip behind.
      jest.useFakeTimers();
      const onValues = jest.fn();
      render(
        <Harness
          filterData={staffSchema}
          initial={{
            [STAFF]: { path: STAFF, type: "integer", min: 1, max: 50 },
          }}
          onValues={onValues}
        />,
      );

      const startup = screen.getByRole("button", { name: "Startup (1–50)" });
      expect(startup).toHaveAttribute("aria-pressed", "true");
      fireEvent.click(startup);
      act(() => {
        jest.advanceTimersByTime(300);
      });

      expect(onValues).toHaveBeenLastCalledWith({});
      expect(startup).toHaveAttribute("aria-pressed", "false");
    });

    it("mark a preset picked when typed bounds match it", () => {
      render(
        <Harness
          filterData={staffSchema}
          initial={{ [STAFF]: { path: STAFF, type: "integer", min: 3001 } }}
        />,
      );

      expect(
        screen.getByRole("button", { name: "Enterprise (>3,000)" }),
      ).toHaveAttribute("aria-pressed", "true");
    });
  });

  describe("date ranges", () => {
    const DATE = "value.date.value";
    const dateSchema = leaves({
      path: DATE,
      type: "date",
      min: "2020-01-01",
      max: "2026-09-01",
    });

    beforeEach(() => {
      jest.useFakeTimers();
      jest.setSystemTime(new Date(2026, 8, 15, 12));
    });

    it("offer presets that look back from today", () => {
      const onValues = jest.fn();
      render(<Harness filterData={dateSchema} onValues={onValues} />);

      fireEvent.click(screen.getByRole("button", { name: "Past 30 days" }));

      expect(onValues).toHaveBeenLastCalledWith({
        [DATE]: { path: DATE, type: "date", min: "2026-08-16" },
      });
      expect(
        screen.getByRole("button", { name: "Past 30 days" }),
      ).toHaveAttribute("aria-pressed", "true");
      // "value.date" is labelled "Event Date".
      expect(screen.getByLabelText("Event Date, from")).toHaveValue(
        "2026-08-16",
      );
    });

    it("compute each preset's start date", () => {
      const onValues = jest.fn();
      render(<Harness filterData={dateSchema} onValues={onValues} />);

      const startOf = (name: string) => {
        fireEvent.click(screen.getByRole("button", { name }));
        return onValues.mock.lastCall[0][DATE].min;
      };

      expect(startOf("Past 90 days")).toBe("2026-06-17");
      expect(startOf("Past 12 months")).toBe("2025-09-15");
      expect(startOf("This year")).toBe("2026-01-01");
    });

    it("clear the filter when the picked preset is clicked again", () => {
      const onValues = jest.fn();
      render(
        <Harness
          filterData={dateSchema}
          initial={{ [DATE]: { path: DATE, type: "date", min: "2026-08-16" } }}
          onValues={onValues}
        />,
      );

      fireEvent.click(screen.getByRole("button", { name: "Past 30 days" }));

      expect(onValues).toHaveBeenLastCalledWith({});
    });

    it("don't mark a preset once an end date is set", () => {
      render(
        <Harness
          filterData={dateSchema}
          initial={{
            [DATE]: {
              path: DATE,
              type: "date",
              min: "2026-08-16",
              max: "2026-08-20",
            },
          }}
        />,
      );

      expect(
        screen.getByRole("button", { name: "Past 30 days" }),
      ).toHaveAttribute("aria-pressed", "false");
    });
  });
});

describe("buildFilterDoc", () => {
  it("rebuilds a shared link's doc exactly, without any group being opened", () => {
    // The regression this guards: a ?f= link's filters live inside collapsed
    // groups, and the next interaction must not rewrite the URL without them.
    expect(buildFilterDoc(schema, filterValuesFromDoc(greenDoc))).toEqual(
      greenDoc,
    );
  });

  it("is null when nothing is set", () => {
    expect(buildFilterDoc(schema, {})).toBeNull();
  });

  it("drops a value whose path the schema no longer has", () => {
    const gone = "value.gone.value";
    expect(
      buildFilterDoc(schema, {
        [gone]: { path: gone, type: "string", allowed_values: ["x"] },
      }),
    ).toBeNull();
  });

  it("lists leaves before groups, as the filter tree renders them", () => {
    const LEAF = "value.b.value";
    const NESTED = "value.grp.a.value";
    const mixed: FilterInterface = {
      path: "value",
      children: [
        {
          path: "value.grp",
          children: [{ path: NESTED, allowed_values: ["A"] }],
        },
        { path: LEAF, allowed_values: ["B"] },
      ],
    };

    const doc = buildFilterDoc(mixed, {
      [NESTED]: { path: NESTED, allowed_values: ["A"] },
      [LEAF]: { path: LEAF, allowed_values: ["B"] },
    });

    expect(doc).toMatchObject({
      children: [{ path: LEAF }, { path: "value.grp" }],
    });
  });
});

describe("range filters from a shared link", () => {
  afterEach(() => {
    jest.useRealTimers();
  });

  it("keeps a range's value after mount instead of resetting it", () => {
    // The old slider reset itself to the schema bounds on mount, and its
    // debounced change handler then rewrote the URL without the filter.
    jest.useFakeTimers();
    const SIZE = "value.size.value";
    const onValues = jest.fn();
    render(
      <Harness
        filterData={leaves({ path: SIZE, type: "number", min: 0, max: 100 })}
        initial={{ [SIZE]: { path: SIZE, type: "number", max: 40 } }}
        onValues={onValues}
      />,
    );

    act(() => {
      jest.advanceTimersByTime(1000);
    });

    expect(onValues).not.toHaveBeenCalled();
    expect(
      screen.getByRole("spinbutton", { name: "Size, maximum" }),
    ).toHaveValue(40);
  });
});
